import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ModelCard from './ModelCard';
import { advertisementService } from '../../../services/advertisementService';
import { useAuth } from '../../../contexts/AuthContext';

const sanitizeBannerUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  if (url.includes('rocket.new')) {
    return '/assets/images/ad-fallback.jpg';
  }
  return url.replace(/ /g, '%20');
};

const SingleBannerSlide = ({ item, isHalfWidth = false, onAction }) => {
  if (!item) return null;

  const bgUrl = item?.backgroundImageUrl || item?.background_image_url || '';
  const imgPos = item?.imagePosition || item?.image_position || 'center';
  const textPos = item?.textPosition || item?.text_position || 'center';
  const imageFit = item?.imageFit || item?.image_fit || 'contain';
  const overlayShade = item?.overlayShade || item?.overlay_shade || 'none';
  const hasButton = !!item?.actionButtonText?.trim();

  const bgPosition = 
    imgPos === 'left' ? 'left center' :
    imgPos === 'right' ? 'right center' :
    'center center';

  return (
    <div 
      className={`relative ${isHalfWidth ? 'h-64 md:h-72 lg:h-80 w-full' : 'h-64 md:h-80 lg:h-96 w-full'} rounded-2xl overflow-hidden flex items-center bg-slate-900/10 cursor-pointer`}
      onClick={() => onAction && onAction(item)}
    >
      {/* 1. Ambient blurred backdrop when using contain */}
      {imageFit !== 'cover' && bgUrl && (
        <div
          className="absolute inset-0 bg-cover bg-center blur-2xl opacity-25 scale-110 pointer-events-none"
          style={{
            backgroundImage: `url(${sanitizeBannerUrl(bgUrl)})`,
          }}
        />
      )}

      {/* 2. Main Background Image */}
      {bgUrl && (
        <div
          className="absolute inset-0 transition-all duration-500"
          style={{
            backgroundImage: `url(${sanitizeBannerUrl(bgUrl)})`,
            backgroundSize: imageFit === 'cover' ? 'cover' : 'contain',
            backgroundPosition: bgPosition,
            backgroundRepeat: 'no-repeat',
            filter: overlayShade === 'dark' 
              ? 'brightness(0.7)' 
              : (overlayShade === 'medium' ? 'brightness(0.85)' : 'none')
          }}
        />
      )}

      {/* 3. Configurable Overlay Shading */}
      {(!overlayShade || overlayShade === 'none') ? null :
       overlayShade === 'subtle' ? (
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/10 to-transparent pointer-events-none" />
      ) : overlayShade === 'medium' ? (
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/25 to-transparent pointer-events-none" />
      ) : overlayShade === 'bottom' ? (
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent pointer-events-none" />
      )}

      {/* 4. Text & Button Overlay */}
      {textPos !== 'none' && (
        <div className={`relative h-full w-full flex items-center z-10 ${
          textPos === 'left' ? 'justify-start pl-8 md:pl-16 text-left' :
          textPos === 'right' ? 'justify-end pr-8 md:pr-16 text-right' :
          'justify-center text-center'
        }`}>
          <div className={`max-w-2xl px-6 text-white ${
            textPos === 'left' ? 'text-left items-start' :
            textPos === 'right' ? 'text-right items-end' :
            'text-center items-center mx-auto'
          } flex flex-col`}>
            {/* Badge */}
            {item?.badgeText && (
              <div className={`w-full flex ${
                textPos === 'left' ? 'justify-start' :
                textPos === 'right' ? 'justify-end' :
                'justify-center'
              } mb-3`}>
                <span className={`${item?.badgeColor || 'bg-blue-600'} text-white px-3 py-1 rounded-full text-xs font-semibold shadow-xs`}>
                  {item.badgeText}
                </span>
              </div>
            )}

            {/* Title */}
            {(item?.showTitle !== false && item?.show_title !== false) && item?.title && (
              <h2 className={`w-full text-2xl md:text-3xl lg:text-4xl font-bold mb-2 leading-tight drop-shadow-md text-white ${
                textPos === 'left' ? 'text-left' :
                textPos === 'right' ? 'text-right' :
                'text-center'
              }`}>
                {item?.title}
              </h2>
            )}

            {/* Description */}
            {item?.description && (
              <p className={`w-full text-sm md:text-base text-gray-100 mb-4 line-clamp-2 drop-shadow ${
                textPos === 'left' ? 'text-left' :
                textPos === 'right' ? 'text-right' :
                'text-center'
              }`}>
                {item?.description}
              </p>
            )}

            {/* CTA Button */}
            {hasButton && (
              <div className={`w-full flex ${
                textPos === 'left' ? 'justify-start' :
                textPos === 'right' ? 'justify-end' :
                'justify-center'
              }`}>
                <Button
                  size={isHalfWidth ? "sm" : "lg"}
                  iconName={item?.actionType === 'modal' ? 'Maximize2' : 'ArrowRight'}
                  iconPosition="right"
                  className="bg-white text-primary hover:bg-gray-100 border-none shadow-md transform transition hover:scale-105"
                  onClick={(e) => {
                    e?.stopPropagation?.();
                    if (onAction) onAction(item);
                  }}
                >
                  {item.actionButtonText}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PromotedProductsBanner = ({
  onAddToCart,
  onAddToWishlist,
  onAddToCollection,
  onRemoveFromCollection,
  collectionItems,
  onQuickPreview,
  isAuthenticated,
  onAuthRequired,
  bannerLocation = 'homepage_carousel',
  fallbackDuration = 5000,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [banners, setBanners] = useState([]);
  const [selectedBanner, setSelectedBanner] = useState(null); // For modal

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const activeBanners = await advertisementService.getActiveBanners();

        const filtered = (activeBanners || []).filter(b => {
          if (bannerLocation === 'homepage_carousel') {
            return b.location === 'homepage_carousel' || !b.location;
          }
          return b.location === bannerLocation;
        });

        // Sort by display_order
        filtered.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
        setBanners(filtered);
      } catch (error) {
        console.error('Failed to fetch advertisement banners:', error);
      }
    };

    fetchBanners();
  }, [bannerLocation]);

  const currentItem = banners[currentSlide];

  // Side-by-Side Dual Banner Detection:
  const isDuo = banners.length >= 2 && (
    currentItem?.adWidth === 'half' || 
    banners.some(b => b.adWidth === 'half')
  );

  const slideIncrement = isDuo ? 2 : 1;

  // Record impression when slide changes
  useEffect(() => {
    if (currentItem && isVisible) {
      advertisementService.recordImpression(currentItem.id, user?.id);
      if (isDuo && banners[(currentSlide + 1) % banners.length]) {
        advertisementService.recordImpression(banners[(currentSlide + 1) % banners.length].id, user?.id);
      }
    }
  }, [currentSlide, currentItem, isVisible, user, isDuo, banners]);

  useEffect(() => {
    if (isPaused || banners.length <= (isDuo ? 2 : 1)) return;

    const slideDuration = currentItem?.duration || fallbackDuration;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + slideIncrement) % banners.length);
    }, slideDuration);

    return () => clearInterval(timer);
  }, [isPaused, banners.length, currentItem, fallbackDuration, isDuo, slideIncrement]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - slideIncrement + banners.length) % banners.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + slideIncrement) % banners.length);
  };

  const handleAction = (banner) => {
    advertisementService.recordClick(banner.id, user?.id);

    if (banner.actionType === 'link' && banner.targetLink) {
      navigate(banner.targetLink);
    } else if (banner.actionType === 'modal') {
      setSelectedBanner(banner);
      setIsPaused(true);
    }
  };

  if (!isVisible || banners.length === 0) return null;

  const nextItem = isDuo && banners.length >= 2 ? banners[(currentSlide + 1) % banners.length] : null;

  return (
    <>
      <div
        className="relative w-full rounded-2xl overflow-hidden mb-8 shadow-lg"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}>

        {/* Dual Side-by-Side or Single Banner Layout */}
        {isDuo && nextItem ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 w-full">
            <SingleBannerSlide 
              item={currentItem} 
              isHalfWidth={true} 
              onAction={handleAction} 
            />
            <SingleBannerSlide 
              item={nextItem} 
              isHalfWidth={true} 
              onAction={handleAction} 
            />
          </div>
        ) : (
          <SingleBannerSlide 
            item={currentItem} 
            isHalfWidth={false} 
            onAction={handleAction} 
          />
        )}

        {/* Navigation Arrows (if multiple slides exist) */}
        {banners.length > (isDuo ? 2 : 1) && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-md text-white p-2.5 rounded-full transition-all border border-white/20 shadow-md cursor-pointer z-20"
              aria-label="Previous slide">
              <Icon name="ChevronLeft" size={20} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-md text-white p-2.5 rounded-full transition-all border border-white/20 shadow-md cursor-pointer z-20"
              aria-label="Next slide">
              <Icon name="ChevronRight" size={20} />
            </button>
          </>
        )}

        {/* Dots Indicator */}
        {banners.length > (isDuo ? 2 : 1) && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-20">
            {banners.map((_, index) => {
              if (isDuo && index % 2 !== 0) return null;
              return (
                <button
                  key={index}
                  onClick={(e) => { e.stopPropagation(); goToSlide(index); }}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 shadow-sm cursor-pointer ${
                    index === currentSlide ? 'bg-white w-7' : 'bg-white/50 hover:bg-white/80'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              );
            })}
          </div>
        )}

        {/* Pause/Play Button */}
        {banners.length > (isDuo ? 2 : 1) && (
          <button
            onClick={(e) => { e.stopPropagation(); setIsPaused(!isPaused); }}
            className="absolute bottom-4 right-4 bg-white/20 hover:bg-white/40 backdrop-blur-md text-white p-1.5 rounded-full transition-all border border-white/20 cursor-pointer z-20"
            aria-label={isPaused ? 'Play carousel' : 'Pause carousel'}>
            <Icon name={isPaused ? 'Play' : 'Pause'} size={14} />
          </button>
        )}
      </div>

      {/* Banner Modal */}
      {selectedBanner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-background w-full max-w-6xl max-h-[90vh] rounded-xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="relative h-48 md:h-64 flex-shrink-0">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${sanitizeBannerUrl(selectedBanner.backgroundImageUrl)})`,
                }} />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-black/60 to-transparent" />
              <button
                onClick={() => setSelectedBanner(null)}
                className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full backdrop-blur-md transition-colors z-10"
              >
                <Icon name="X" size={24} />
              </button>
              
              <div className="absolute bottom-0 left-0 right-0 p-8 text-left">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">{selectedBanner.title}</h2>
                <p className="text-xl text-gray-200">{selectedBanner.description}</p>
              </div>
            </div>

            {/* Modal Content - Curated Products */}
            <div className="flex-1 overflow-y-auto p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-foreground">Curated Collection</h3>
                <span className="text-muted-foreground">{selectedBanner.products?.length || 0} items</span>
              </div>
              
              {selectedBanner.products && selectedBanner.products.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {selectedBanner.products.map(product => (
                    <ModelCard
                      key={product.id}
                      model={product}
                      viewMode="grid"
                      onAddToCart={onAddToCart}
                      onAddToWishlist={onAddToWishlist}
                      onAddToCollection={onAddToCollection}
                      onRemoveFromCollection={onRemoveFromCollection}
                      isInCollection={collectionItems?.includes(product.id)}
                      onQuickPreview={onQuickPreview}
                      isSelected={false}
                      onSelect={() => {}}
                      isAuthenticated={isAuthenticated}
                      onAuthRequired={onAuthRequired}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-muted/30 rounded-lg border border-dashed border-muted-foreground/20">
                  <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4 opacity-50" />
                  <p className="text-muted-foreground text-lg">No products currently assigned to this collection.</p>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-muted/10 flex justify-end">
               <Button onClick={() => setSelectedBanner(null)} variant="outline">Close</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PromotedProductsBanner;
